import { useState, type KeyboardEvent } from 'react'
import { Database, Cloud, X } from 'lucide-react'
import { Button } from '../primitives/Button'
import { Hint, Label, TextArea, TextInput } from '../primitives/Field'
import { Segmented } from '../primitives/Segmented'
import { TagPill } from './PromptsView'

export interface PromptDraft {
  id?: string
  name: string
  command: string
  description: string
  body: string
  tags: string[]
  source: 'local' | 'server'
}

export interface PromptEditorProps {
  initial?: Partial<PromptDraft>
  onSave: (draft: PromptDraft) => void
  onCancel: () => void
}

export function PromptEditor({ initial, onSave, onCancel }: PromptEditorProps) {
  const [name, setName] = useState(initial?.name ?? '')
  const [command, setCommand] = useState(initial?.command ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [body, setBody] = useState(initial?.body ?? '')
  const [tags, setTags] = useState<string[]>(initial?.tags ?? [])
  const [source, setSource] = useState<'local' | 'server'>(initial?.source ?? 'local')
  const [newTag, setNewTag] = useState('')

  const addTag = () => {
    const t = newTag.trim()
    if (t && !tags.includes(t)) setTags([...tags, t])
    setNewTag('')
  }
  const onTagKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag()
    }
  }

  const save = () => {
    onSave({ id: initial?.id, name, command, description, body, tags, source })
  }

  return (
    <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
      <div>
        <Label>Name</Label>
        <TextInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Formeller Ton"
        />
      </div>

      <div>
        <Label>Befehl</Label>
        <TextInput
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          placeholder="/formeller-ton"
          className="font-medium text-brand-700"
        />
        <Hint>
          Im Chat per <span className="font-medium text-brand-600">{command || '/befehl'}</span>{' '}
          aufrufbar.
        </Hint>
      </div>

      <div>
        <Label>Beschreibung</Label>
        <TextInput
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Schreibt die Auswahl in einen formellen Ton um."
        />
      </div>

      <div>
        <Label>Prompt</Label>
        <TextArea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder="Formuliere den folgenden Abschnitt … {{auswahl}}"
        />
        <Hint>
          Variablen: <span className="font-medium text-brand-600">{'{{auswahl}}'}</span> ·{' '}
          <span className="font-medium text-brand-600">{'{{dokument}}'}</span>
        </Hint>
      </div>

      <div>
        <Label>Tags</Label>
        <div className="flex flex-wrap items-center gap-1.5">
          {tags.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1">
              <TagPill tag={tag} />
              <button
                type="button"
                onClick={() => setTags(tags.filter((t) => t !== tag))}
                aria-label={`${tag} entfernen`}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={onTagKey}
            onBlur={addTag}
            placeholder="+ Tag"
            className="w-20 rounded-full border border-dashed border-neutral-300 px-2 py-1 text-xs focus:border-brand-300 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <Label>Speicherort</Label>
        <Segmented
          value={source}
          onChange={setSource}
          options={[
            { value: 'local', label: 'Lokal', icon: <Database className="h-3.5 w-3.5" /> },
            { value: 'server', label: 'Server', icon: <Cloud className="h-3.5 w-3.5" /> },
          ]}
        />
      </div>

      <div className="flex gap-2 pt-1">
        <Button variant="primary" className="flex-1" onClick={save} disabled={!name.trim()}>
          Speichern
        </Button>
        <Button onClick={onCancel}>Abbrechen</Button>
      </div>
    </div>
  )
}
